import React, { useState, useEffect } from "react";
import { Table, message, Spin, Button, Tooltip } from "antd";
import axios from "axios";
import { BASE_URL } from "../../Apis/BaseUrl";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import TopNavbar from "../../Component/TopMenu/TopNavbar";
import NavbarSection from "../../Component/NavbarSection/NavbarSection";
import { Helmet } from "react-helmet";
import FranchiseInfoModal from "./FranchiseInfoModal";

const Franchise = () => {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedFranchiseInfo, setSelectedFranchiseInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFranchises = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get("v&b_token")
          ? JSON.parse(Cookies.get("v&b_token"))?.token
          : null;
        if (!token) {
          throw new Error("Admin token not found");
        }

        const response = await axios.get(`${BASE_URL}/franchise/list`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setFranchises(response.data);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to fetch franchises";
        setError(errorMessage);
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchFranchises();
  }, []);

  const handleDelete = async (id) => {
    try {
      const token = Cookies.get("v&b_token")
        ? JSON.parse(Cookies.get("v&b_token"))?.token
        : null;
      if (!token) {
        throw new Error("Admin token not found");
      }
      const response = await axios.delete(`${BASE_URL}/franchise/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        message.success("Franchise deleted successfully");
        setFranchises(franchises.filter((f) => f.id !== id));
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to delete franchise";
      setError(errorMessage);
      message.error(errorMessage);
    }
  };

  const handleViewInfo = async (franchiseId) => {
    setLoadingInfo(true);
    try {
      const token = Cookies.get("v&b_token")
        ? JSON.parse(Cookies.get("v&b_token"))?.token
        : null;
      if (!token) {
        throw new Error("Admin token not found");
      }

      const response = await axios.get(`${BASE_URL}/franchise/${franchiseId}/business-info`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSelectedFranchiseInfo(response.data.message);
      setInfoModalOpen(true);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch franchise info";
      message.error(errorMessage);
    } finally {
      setLoadingInfo(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(amount || 0);
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "sn",
      key: "sn",
      width: 60,
      align: "center",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 120,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
    },
    {
      title: "Password",
      dataIndex: "password",
      key: "password",
      width: 100,
      render: (password) => (
        <Tooltip title={password}>
          <span
            style={{
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "15px",
            }}
          >
            {password ? "********" : "No Password"}
            <button
              className="px-2 py-1 rounded-1 border-0"
              style={{
                backgroundColor: "#AE0000",
                color: "white",
                fontSize: "12px",
              }}
              onClick={() => {
                navigator.clipboard.writeText(password);
                message.success("Password copied to clipboard");
              }}
            >
              Copy
            </button>
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Sellers",
      dataIndex: "sellersDisplay",
      key: "sellersDisplay",
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text || "No Sellers"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Total Commission",
      dataIndex: "commission",
      key: "commission",
      width: 130,
      align: "right",
      render: (commission) => formatCurrency(commission),
    },
    {
      title: "Total Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      width: 130,
      align: "right",
      render: (revenue) => formatCurrency(revenue),
    },
    {
      title: "Sellers Count",
      dataIndex: "sellersCount",
      key: "sellersCount",
      width: 100,
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (text, record) => (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            className="px-2 py-1 rounded-1 border-0"
            style={{
              backgroundColor: "#007bff",
              color: "white",
              fontSize: "12px",
            }}
            onClick={() => handleViewInfo(record.id)}
            disabled={loadingInfo}
          >
            {loadingInfo ? "..." : "Info"}
          </button>
          <button
            className="px-2 py-1 rounded-1 border-0"
            style={{
              backgroundColor: "#AE0000",
              color: "white",
              fontSize: "12px",
            }}
            onClick={() => navigate(`/franchise/edit/${record.id}`)}
          >
            Edit
          </button>
          <button
            className="px-2 py-1 rounded-1 border-0"
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              fontSize: "12px",
            }}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div>
        <Helmet>
          <title>Franchise Management</title>
        </Helmet>
        <TopNavbar />
        <NavbarSection />
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin tip="Loading franchises..." size="large">
            <div style={{ minHeight: "200px" }} />
          </Spin>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Helmet>
          <title>Franchise Management</title>
        </Helmet>
        <TopNavbar />
        <NavbarSection />
        <div style={{ padding: "40px", color: "red", textAlign: "center" }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if(franchises.length === 0){
    return (
      <div>
        <Helmet>
          <title>Franchise Management</title>
        </Helmet>
        <TopNavbar />
        <NavbarSection />
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h6 style={{ color: "#AE0000", fontSize: "1.3rem", fontWeight: "600", margin: 0 }}>
            Franchise Management
          </h6>
          <p style={{ color: "#666", fontSize: "1rem", margin: 0 }}>
            No franchises found.
          </p>
        </div>
      </div>
    );
  }
    return (
    <div>
      <Helmet>
        <title>Franchise Management</title>
      </Helmet>
      <TopNavbar />
      <NavbarSection />

      {/* Hero Section Container */}
      <div style={{ padding: "30px 40px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
          }}
        >
          <h6
            style={{
              color: "#AE0000",
              fontSize: "1.3rem",
              fontWeight: "600",
              margin: 0,
            }}
          >
            Franchise Management
          </h6>
          <button
            className="px-3 py-2 rounded-1 border-0"
            style={{ backgroundColor: "#AE0000", color: "white" }}
            onClick={() => navigate("/franchise/add")}
          >
            Add New Franchise
          </button>
        </div>

        {/* Table Container with padding */}
        <div
          style={{
            border: "1px solid #E0E1E0",
            borderRadius: "6px",
            padding: "20px",
            backgroundColor: "white",
          }}
        >
          <Table
            dataSource={franchises.map((f) => {
              const calculatedTotalCommission =
                f.sellers?.reduce((total, seller) => {
                  return total + (seller.commission || 0);
                }, 0) || 0;

              return {
                ...f,
                key: f.id,
                commission: calculatedTotalCommission,
              };
            })}
            columns={columns}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} franchises`,
            }}
            bordered
            scroll={{ x: 1200 }}
            size="small"
          />
        </div>
      </div>

        <FranchiseInfoModal
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
          franchiseInfo={selectedFranchiseInfo}
        />
    </div>
  );
};

export default Franchise;
